/**
 * FileChangesTree Component
 * 
 * Renders a tree of file changes with:
 * - Collapsible folders
 * - Color-coded change indicators (green=added, yellow=modified, red=deleted)
 * - Click to view file content
 */

import { useState } from 'react';
import { FileChangeNode, FileChangeType } from '../../../../types/AppPanel';

interface FileChangesTreeProps {
  /** Tree nodes from the API */
  nodes: FileChangeNode[];
  /** Callback when a file is clicked to view */
  onViewFile: (node: FileChangeNode) => void;
  /** Callback when a file is clicked to view diff (for modified files) */
  onViewDiff?: (node: FileChangeNode) => void;
}

/**
 * Map node type to semantic change type
 */
function getChangeType(nodeType: string): FileChangeType {
  switch (nodeType) {
    case 'green-file': return 'added';
    case 'yellow-file': return 'modified';
    case 'red-file': return 'deleted';
    default: return 'unchanged';
  }
}

/**
 * Get visual styles for change type
 */
function getChangeStyles(changeType: FileChangeType): { symbol: string; color: string } {
  switch (changeType) {
    case 'added':
      return { symbol: '+', color: 'text-emerald-400' };
    case 'modified':
      return { symbol: '~', color: 'text-amber-400' };
    case 'deleted':
      return { symbol: '-', color: 'text-red-400' };
    default:
      return { symbol: '', color: 'text-white/40' };
  }
}

interface TreeNodeProps {
  node: FileChangeNode;
  depth: number;
  onViewFile: (node: FileChangeNode) => void;
  onViewDiff?: (node: FileChangeNode) => void;
}

/**
 * Single tree node (file or folder)
 */
function TreeNode({ node, depth, onViewFile, onViewDiff }: TreeNodeProps) {
  const [isOpen, setIsOpen] = useState(node.state?.opened ?? true);
  const isFolder = node.type === 'folder';
  const changeType = getChangeType(node.type);
  const { symbol, color } = getChangeStyles(changeType);
  const canView = !isFolder && node.data && changeType !== 'deleted';
  const canDiff = !isFolder && node.data && changeType === 'modified' && onViewDiff;

  return (
    <div>
      {/* Node Row */}
      <div
        className={`
          flex items-center gap-2 py-1.5 px-2 rounded
          transition-colors duration-150
          ${isFolder ? 'hover:bg-white/5 cursor-pointer' : ''}
          ${!isFolder && node.data ? 'hover:bg-white/5 group' : ''}
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => {
          if (isFolder) {
            setIsOpen(!isOpen);
          }
        }}
      >
        {/* Folder chevron / File indicator */}
        {isFolder ? (
          <>
            <svg 
              width="12" 
              height="12" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className={`text-white/40 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
            >
              <path d="M6 9l6 6 6-6"/>
            </svg>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-amber-400/70">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" fill="currentColor"/>
            </svg>
          </>
        ) : (
          <>
            {/* Change Indicator */}
            <span className={`w-4 text-center font-mono text-xs font-bold ${color}`}>
              {symbol}
            </span>
            {/* File Icon */}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className={color}>
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2"/>
              <path d="M14 2v6h6" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </>
        )}

        {/* Name */}
        <span className={`text-sm flex-1 ${isFolder ? 'text-white/70' : color}`}>
          {node.text}
        </span>

        {/* Action Buttons (for files) */}
        {!isFolder && node.data && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {/* Diff Button (for modified files) */}
            {canDiff && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewDiff(node);
                }}
                className="px-2 py-0.5 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded transition-colors"
                title="View diff"
              >
                Diff
              </button>
            )}
            {/* View Button (for non-deleted files) */}
            {canView && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onViewFile(node);
                }}
                className="px-2 py-0.5 text-xs text-[#208AEC] hover:text-[#60a5fa] hover:bg-[#208AEC]/10 rounded transition-colors"
                title="View file"
              >
                View
              </button>
            )}
          </div>
        )}
      </div>

      {/* Children (for folders) */}
      {isFolder && isOpen && node.children && (
        <div>
          {node.children.map((child, index) => (
            <TreeNode
              key={`${child.text}-${index}`}
              node={child}
              depth={depth + 1}
              onViewFile={onViewFile}
              onViewDiff={onViewDiff}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FileChangesTree({ nodes, onViewFile, onViewDiff }: FileChangesTreeProps) {
  if (!nodes || nodes.length === 0) {
    return (
      <div className="py-4 text-center text-white/40 text-sm">
        No file changes in this release
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-black/20 border border-white/5 overflow-hidden">
      <div className="py-2">
        {nodes.map((node, index) => (
          <TreeNode
            key={`${node.text}-${index}`}
            node={node}
            depth={0}
            onViewFile={onViewFile}
            onViewDiff={onViewDiff}
          />
        ))}
      </div>
    </div>
  );
}

